import { useEffect, useState } from 'react';
import { Link } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { CONTACT_KIND_LABELS, type ContactSuggestion } from '@kontor/shared';
import { api, queryString } from '../../api';
import './contacts.css';

/** Wert erst übernehmen, wenn die Eingabe kurz ruht, damit nicht bei jedem Tastendruck eine Abfrage läuft */
function useSettled(value: string, ms = 500): string {
  const [settled, setSettled] = useState(value);
  useEffect(() => {
    const timer = window.setTimeout(() => setSettled(value), ms);
    return () => window.clearTimeout(timer);
  }, [value, ms]);
  return settled;
}

/**
 * Warnung im Kontaktformular (KON-049): Steht die E-Mail-Adresse oder Telefonnummer schon bei einem anderen Kontakt, zeigt sie ihn an. Speichern bleibt
 * möglich, denn manchmal teilen sich zwei Personen eine Nummer. Beim Bearbeiten zählt der Kontakt selbst nicht.
 */
export function DuplicateWarning({ email, phone, excludeId }: { email: string; phone: string; excludeId?: string }) {
  const settledEmail = useSettled(email.trim());
  const settledPhone = useSettled(phone.trim());
  // Erst abfragen, wenn die Eingabe nach einer E-Mail-Adresse oder Telefonnummer aussieht
  const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(settledEmail);
  const phoneOk = settledPhone.replace(/\D/g, '').length >= 6;

  const found = useQuery({
    queryKey: ['contact-duplicates', { email: emailOk ? settledEmail : '', phone: phoneOk ? settledPhone : '', excludeId }],
    queryFn: async () => (await api<{ data: ContactSuggestion[] }>('GET', `/contacts/suggestions${queryString({ email: emailOk ? settledEmail : undefined, phone: phoneOk ? settledPhone : undefined, excludeId })}`)).data,
    enabled: emailOk || phoneOk,
  });

  if (!found.data || found.data.length === 0 || (!emailOk && !phoneOk)) return null;
  const reason = (c: ContactSuggestion) => c.matchedBy.map((m) => (m === 'email' ? 'gleiche E-Mail-Adresse' : 'gleiche Telefonnummer')).join(' und ');

  return (
    <div className="alert alert--warn" role="status">
      <strong>Möglicherweise gibt es diesen Kontakt schon.</strong>
      <ul className="duplicate-list">
        {found.data.map((c) => (
          <li key={c.id}>
            <Link to={`/kontakte/${c.id}`} target="_blank" rel="noreferrer" className="strong-link">{c.name}</Link>
            <span className="muted small"> ({CONTACT_KIND_LABELS[c.kind]}, {reason(c)})</span>
          </li>
        ))}
      </ul>
      <span className="small">Sie können trotzdem speichern. Zwei Kontakte lassen sich später auf der Karte eines Kontakts zusammenführen.</span>
    </div>
  );
}
